import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ElevatedButton, TextInput } from '@producer/ui-kit';

import { observer } from 'mobx-react-lite';
import { SettingScreenLayout } from '../../../features/settings/components/setting-screen-layout';
import { updateSpacePicture } from '../../../features/spaces/stores/space.slice';
import { SettingsAvatar } from '../../../components/settings-avatar/settings-avatar';
import { uiStore } from '../../../core/stores/ui-store';
import { useResponsive } from '../../../hooks/useResponsive';
import { useCurrentSpace } from '../../../hooks/use-current-space';

import styles from './space-settings.route.module.scss';
type FormValues = {
  name: string;
};

export const SpaceSettingsRoute: React.FC = observer(() => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const space = useCurrentSpace();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    uiStore.pageTitle = {
      label: 'General',
    };

    uiStore.showBack = true;
    uiStore.action = handleSubmit(onHandleSubmit);

    uiStore.currentLocation = 'space.settings';

    return () => {
      uiStore.action = undefined;
      uiStore.currentLocation = '';
    };
  }, []);

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .matches(/^(.*)?\S+(.*)?$/, t('common:fieldRequired'))
          .required(t('common:fieldRequired'))
          .min(4, t('common:fieldMin', { min: 4 }))
          .max(32, t('common:fieldMax', { max: 32 })),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { name: space.name },
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onHandleSubmit = async (values: any) => {
    if (isLoading && values.name) {
      return;
    }

    setIsLoading(true);

    await space.update({ name: values.name });

    setIsLoading(false);
  };

  const handleSetLogo = async (file: File) => {
    await updateSpacePicture({
      space,
      file,
      type: 'logo',
    });
  };

  const handleSetFavicon = async (file: File) => {
    await updateSpacePicture({
      space,
      file,
      type: 'avatar',
    });
  };

  return (
    <SettingScreenLayout title={isDesktop ? 'General' : ''}>
      <Container>
        <Description>
          Edit your workspace name, upload a large logo for your workspace cover and a small one for
          sidebar.
        </Description>

        <Form onSubmit={handleSubmit(onHandleSubmit)}>
          <FieldContainer>
            <TextInput
              {...register('name')}
              placeholder={'Space name'}
              label={'Creative space name'}
              errorMessage={errors.name?.message}
              required
            />
          </FieldContainer>

          <DownloadImagesContainer>
            <CompanyIconWrapper>
              <SettingsAvatar
                placeholder="text"
                defaultUrl={space?.avatar}
                onChange={handleSetFavicon}
                title="Company icon"
                detail="80x80 px"
              />
            </CompanyIconWrapper>

            <CompanyLogoWrapper>
              <SettingsAvatar
                placeholder="text"
                defaultUrl={space.logo}
                onChange={handleSetLogo}
                title="Company logo"
                detail="16:9, 800x450px"
                aspectRatio={{
                  minimum: 1,
                  maximum: 4.5,
                }}
                theme="light"
              />
            </CompanyLogoWrapper>
          </DownloadImagesContainer>

          <ButtonGroupContainer>
            <ElevatedButton text="Save" type="submit" isLoading={isLoading} />
          </ButtonGroupContainer>
        </Form>
      </Container>
    </SettingScreenLayout>
  );
});

const Container = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['container']} ${className}` : styles['container']}
    {...props}
  />
);

const Description = ({ className, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
  <p
    className={className ? `${styles['description']} ${className}` : styles['description']}
    {...props}
  />
);

const DownloadImagesContainer = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className
        ? `${styles['download-images-container']} ${className}`
        : styles['download-images-container']
    }
    {...props}
  />
);

const CompanyIconWrapper = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className ? `${styles['company-icon-wrapper']} ${className}` : styles['company-icon-wrapper']
    }
    {...props}
  />
);

const CompanyLogoWrapper = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className ? `${styles['company-logo-wrapper']} ${className}` : styles['company-logo-wrapper']
    }
    {...props}
  />
);

const ButtonGroupContainer = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className
        ? `${styles['button-group-container']} ${className}`
        : styles['button-group-container']
    }
    {...props}
  />
);

export const FieldContainer = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['field-container']} ${className}` : styles['field-container']}
    {...props}
  />
);

export const Form = ({ className, ...props }: React.ComponentPropsWithoutRef<'form'>) => (
  <form className={className ? `${styles['form']} ${className}` : styles['form']} {...props} />
);
